// arcSwipeDetector — pure detector för 1-finger båg-svep som triggar
// sheet-byte på mobil.
//
// Vad räknas som en båge:
//   FWD (nästa sheet, 1→2→3): start i BR-kvadranten, slut i TL-kvadranten,
//     path-mittpunkt sitter mätbart längre HÖGER än chord-mitten
//     (= path böjer ut åt höger).
//   BACK (föregående sheet): start i BL, slut i TR, path-mittpunkt
//     sitter mätbart längre VÄNSTER än chord-mitten.
//
// Krav (alla måste uppfyllas):
//   1. start- och slutpunkter i rätt kvadranter
//   2. chord-längd ≥ MIN_DISTANCE
//   3. arc-ness (max-avstånd från chord) ≥ ARC_FACTOR × chord-length
//
// Förebild: gesture-libraries använder typ samma sak fast med
// fourier-deskriptorer för full handskriftsmatchning. Här räcker
// 3-punkts-heuristik (start, mitten, slut) som är fast och förutsägbar.

import { quadrantOf, type Quadrant, type Rect, type Point } from './quadrant';

export type ArcDirection = 'fwd' | 'back';

const MIN_DISTANCE = 200;     // px — chord-längd
const ARC_FACTOR = 0.30;       // 30% av chord — kräver tydlig kurvatur
const MIN_PATH_POINTS = 4;     // för få punkter = inte en rörelse

/** Avstånd från punkt p till linjen (a → b). */
function perpendicularDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  // Cross-produkt-formel: |(b-a) × (p-a)| / |b-a|
  const cross = dx * (p.y - a.y) - dy * (p.x - a.x);
  return Math.abs(cross) / Math.sqrt(lenSq);
}

/** Tecken på cross-produkten — +1 om p ligger till höger om a→b, -1 vänster. */
function sideOfChord(p: Point, a: Point, b: Point): 1 | -1 | 0 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const cross = dx * (p.y - a.y) - dy * (p.x - a.x);
  if (cross > 0) return 1;   // höger i skärm-koord (y växer neråt)
  if (cross < 0) return -1;  // vänster
  return 0;
}

/**
 * Detect arc-svep. Returnerar null om path inte matchar, annars riktning.
 */
export function detectArc(path: Point[], viewport: Rect): ArcDirection | null {
  if (path.length < MIN_PATH_POINTS) return null;

  const start = path[0];
  const end = path[path.length - 1];

  // 1. Chord-längd
  const chord = Math.hypot(end.x - start.x, end.y - start.y);
  if (chord < MIN_DISTANCE) return null;

  // 2. Kvadranter — måste ligga i diagonal-par
  const startQ: Quadrant = quadrantOf(start, viewport);
  const endQ: Quadrant = quadrantOf(end, viewport);
  const isFwdQuadrants = startQ === 'BR' && endQ === 'TL';
  const isBackQuadrants = startQ === 'BL' && endQ === 'TR';
  if (!isFwdQuadrants && !isBackQuadrants) return null;

  // 3. Hitta path-mittpunkten — den med största avstånd från chord.
  // (Inte path[middleIndex] eftersom hastigheten kan variera.)
  let maxDist = 0;
  let maxPoint: Point = path[Math.floor(path.length / 2)];
  for (let i = 1; i < path.length - 1; i++) {
    const d = perpendicularDistance(path[i], start, end);
    if (d > maxDist) {
      maxDist = d;
      maxPoint = path[i];
    }
  }

  // 4. Tillräcklig kurvatur?
  if (maxDist < chord * ARC_FACTOR) return null;

  // 5. Sida av chord — i screen-koord (y växer neråt) ger positivt cross
  // att p ligger till HÖGER om chord-vektorn (sett från start mot end).
  //
  // För FWD (BR→TL): user-rörelsen "höger-ner → höger → vänster-uppe"
  // bulgar vid VIEWPORT-höger. Räknat relativt chord-vektorn (som pekar
  // uppåt-vänster) hamnar viewport-höger på höger-sidan av chord:en →
  // positivt cross → side = +1.
  //
  // För BACK (BL→TR): user-rörelsen bulgar vid VIEWPORT-vänster, vilket
  // ligger på vänster-sidan av chord (som pekar uppåt-höger) → negativt
  // cross → side = -1.
  const side = sideOfChord(maxPoint, start, end);

  if (isFwdQuadrants) {
    return side === 1 ? 'fwd' : null;
  }
  return side === -1 ? 'back' : null;
}

// Exporterade konstanter för test + visualisering (GestureTrail kan
// visa "när når man min-distansen" som ett threshold-indikator).
export const ARC_DETECTOR_CONFIG = {
  MIN_DISTANCE,
  ARC_FACTOR,
  MIN_PATH_POINTS,
} as const;
